import { useEffect, useRef, useState } from 'react';
import { TouchPad } from '../../components/TouchPad';
import { arcadeAudio } from '../../platform/audio';
import { fitCanvas, loadImages, rand } from '../../platform/canvas';
import { Input } from '../../platform/input';
import { bestScore } from '../../platform/storage';
import type { GameProps } from '../../platform/types';
import './bloomfall.css';

const W = 1280;
const H = 720;
const WIN = 720;

type Wep = 'ember' | 'spore' | 'roots' | 'thorn' | 'nova';
type Pas = 'heart' | 'magnet' | 'stride' | 'dash' | 'luck';

interface Enemy {
  x: number; y: number; hp: number; max: number; r: number; spd: number; dmg: number;
  kind: 'mite' | 'brute' | 'flyer' | 'boss';
  flash: number; xp: number;
}
interface Gem { x: number; y: number; v: number }
interface Bolt { x: number; y: number; vx: number; vy: number; r: number; dmg: number; life: number; pierce: number; hue: string }
interface Orb { ang: number; dist: number; dmg: number }
interface P { x: number; y: number; vx: number; vy: number; life: number; c: string; s: number }

const UP: Array<{ id: Wep | Pas; name: string; blurb: string; wep?: boolean }> = [
  { id: 'ember', name: 'Ember Needles', blurb: 'Faster homing sparks.', wep: true },
  { id: 'spore', name: 'Orbit Spores', blurb: 'More moons around you.', wep: true },
  { id: 'roots', name: 'Root Pulse', blurb: 'A shockwave every few seconds.', wep: true },
  { id: 'thorn', name: 'Thorn Lance', blurb: 'Piercing shots through the swarm.', wep: true },
  { id: 'nova', name: 'Bloom Nova', blurb: 'Periodic flowerburst.', wep: true },
  { id: 'heart', name: 'Heartwood', blurb: '+25 max HP and a sip of life.' },
  { id: 'magnet', name: 'Pollen Pull', blurb: 'Gems fly farther to you.' },
  { id: 'stride', name: 'Night Stride', blurb: 'Move faster through the grove.' },
  { id: 'dash', name: 'Petal Step', blurb: 'Shorter dash cooldown.' },
  { id: 'luck', name: 'Lucky Dew', blurb: 'Richer gems and rarer hearts.' },
];

export function BloomfallGame({ onExit, onResult }: GameProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const input = useRef(new Input());
  const [paused, setPaused] = useState(false);
  const [choices, setChoices] = useState<typeof UP>([]);
  const [over, setOver] = useState<{ score: number; won: boolean; note: string } | null>(null);
  const hud = useRef({ hp: 100, max: 100, lv: 1, xp: 0, need: 12, t: 0, kills: 0, dash: 1 });
  const pickRef = useRef<(id: Wep | Pas) => void>(() => {});
  const pausedRef = useRef(false);
  const overRef = useRef(false);

  useEffect(() => {
    pausedRef.current = paused || choices.length > 0;
  }, [paused, choices]);

  useEffect(() => {
    const canvas = ref.current!;
    const ctx = fitCanvas(canvas, W, H)!;
    const inp = input.current.attach(canvas);
    let dead = false;
    let acc = 0;

    const player = {
      x: 0, y: 0, r: 16, hp: 100, max: 100, spd: 210, inv: 0, dash: 0, dashCd: 0,
      magnet: 90, luck: 1, lv: 1, xp: 0, need: 12, wep: { ember: 1, spore: 0, roots: 0, thorn: 0, nova: 0 } as Record<Wep, number>,
    };
    const enemies: Enemy[] = [];
    const gems: Gem[] = [];
    const bolts: Bolt[] = [];
    const parts: P[] = [];
    const orbs: Orb[] = [];
    let kills = 0;
    let iFramesBest = 0;
    let iAcc = 0;
    let rootCd = 0;
    let emberCd = 0;
    let thornCd = 0;
    let novaCd = 0;
    let spawnAcc = 0;
    let nextBoss = 120;
    let id = 1;

    const apply = (uid: Wep | Pas) => {
      if (uid === 'ember' || uid === 'spore' || uid === 'roots' || uid === 'thorn' || uid === 'nova') {
        player.wep[uid] += 1;
        if (uid === 'spore') {
          orbs.push({ ang: Math.random() * 6.28, dist: 48 + orbs.length * 10, dmg: 8 + player.wep.spore * 3 });
        }
      } else if (uid === 'heart') {
        player.max += 25;
        player.hp = Math.min(player.max, player.hp + 40);
      } else if (uid === 'magnet') player.magnet += 50;
      else if (uid === 'stride') player.spd += 28;
      else if (uid === 'dash') player.dashCd = Math.max(0.45, player.dashCd);
      else if (uid === 'luck') player.luck += 0.25;
      setChoices([]);
      arcadeAudio.blip(660, 0.08, 'triangle', 0.1);
    };
    pickRef.current = apply;

    const offer = () => {
      const pool = UP.filter((u) => {
        if (u.wep) return player.wep[u.id as Wep] < 6;
        return true;
      });
      const taken: typeof UP = [];
      const copy = [...pool];
      while (taken.length < 3 && copy.length) {
        const i = (Math.random() * copy.length) | 0;
        taken.push(copy.splice(i, 1)[0]);
      }
      setChoices(taken);
      arcadeAudio.blip(520, 0.12, 'sine', 0.1);
    };

    const burst = (x: number, y: number, c: string, n = 8) => {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * 6.28;
        const s = rand(40, 180);
        parts.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: rand(0.25, 0.6), c, s: rand(2, 5) });
      }
    };

    const spawn = (kind: Enemy['kind'], x: number, y: number) => {
      const table = {
        mite: { hp: 12, r: 12, spd: 78, dmg: 7, xp: 1 },
        brute: { hp: 48, r: 20, spd: 52, dmg: 14, xp: 4 },
        flyer: { hp: 18, r: 11, spd: 110, dmg: 6, xp: 2 },
        boss: { hp: 420, r: 36, spd: 46, dmg: 22, xp: 30 },
      }[kind];
      const scale = 1 + acc / 240;
      enemies.push({
        x, y, kind, flash: 0,
        hp: table.hp * scale, max: table.hp * scale,
        r: table.r, spd: table.spd, dmg: table.dmg, xp: table.xp,
      });
    };

    const around = (dist: number) => {
      const a = Math.random() * 6.28;
      return { x: player.x + Math.cos(a) * dist, y: player.y + Math.sin(a) * dist };
    };

    const hurt = (e: Enemy, dmg: number) => {
      e.hp -= dmg;
      e.flash = 0.12;
      if (e.hp <= 0) {
        const i = enemies.indexOf(e);
        if (i >= 0) enemies.splice(i, 1);
        kills += 1;
        gems.push({ x: e.x, y: e.y, v: Math.ceil(e.xp * player.luck) });
        if (Math.random() < 0.04 * player.luck) {
          player.hp = Math.min(player.max, player.hp + 12);
          burst(e.x, e.y, '#ff5d8f', 10);
        }
        burst(e.x, e.y, e.kind === 'boss' ? '#ffb020' : '#9dff7a', e.kind === 'boss' ? 28 : 8);
        arcadeAudio.blip(e.kind === 'boss' ? 140 : 220, 0.05, 'square', 0.05);
      }
    };

    const nearest = () => {
      let best: Enemy | null = null;
      let d = 1e9;
      for (const e of enemies) {
        const dd = (e.x - player.x) ** 2 + (e.y - player.y) ** 2;
        if (dd < d) { d = dd; best = e; }
      }
      return best;
    };

    let imgs = new Map<string, HTMLImageElement>();
    const start = async () => {
      void arcadeAudio.playMusic('bloom', '/assets/music/Infinite-Descent.ogg');
      imgs = await loadImages({
        hero: '/assets/plat/char/character_purple_idle.png',
        walkA: '/assets/plat/char/character_purple_walk_a.png',
        walkB: '/assets/plat/char/character_purple_walk_b.png',
        mite: '/assets/plat/enemy/slime_normal_walk_a.png',
        brute: '/assets/plat/enemy/slime_spike_walk_a.png',
        flyer: '/assets/plat/enemy/bee_a.png',
        boss: '/assets/plat/enemy/slime_block_walk_a.png',
        gem: '/assets/plat/tile/gem_blue.png',
        grass: '/assets/plat/bg/background_color_trees.png',
      });
    };
    void start();

    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      if (dead) return;
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      if (overRef.current) {
        draw();
        raf = requestAnimationFrame(loop);
        return;
      }
      if (!pausedRef.current) tick(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };

    const tick = (dt: number) => {
      acc += dt;
      if (acc > WIN && player.hp > 0) {
        finish(true);
        return;
      }
      const ax = inp.axis();
      let vx = ax.x * player.spd;
      let vy = ax.y * player.spd;
      if ((inp.pressed('space') || inp.actions.a) && player.dash <= 0 && player.dashCd <= 0) {
        player.dash = 0.16;
        player.dashCd = 1.15;
        const m = Math.hypot(ax.x, ax.y) || 1;
        player.x += (ax.x / m) * 70;
        player.y += (ax.y / m) * 70;
        burst(player.x, player.y, '#d7b6ff', 12);
        arcadeAudio.blip(880, 0.06, 'triangle', 0.07);
      }
      if (player.dash > 0) {
        player.dash -= dt;
        vx *= 2.4; vy *= 2.4;
      }
      player.dashCd = Math.max(0, player.dashCd - dt);
      player.x += vx * dt;
      player.y += vy * dt;
      player.inv = Math.max(0, player.inv - dt);
      player.x = Math.max(-1400, Math.min(1400, player.x));
      player.y = Math.max(-1400, Math.min(1400, player.y));

      spawnAcc += dt;
      const rate = Math.max(0.12, 0.55 - acc / 900);
      while (spawnAcc > rate) {
        spawnAcc -= rate;
        const roll = Math.random();
        const k: Enemy['kind'] = acc > 240 && roll < 0.12 ? 'brute' : roll < 0.28 ? 'flyer' : 'mite';
        const p = around(420 + Math.random() * 180);
        spawn(k, p.x, p.y);
      }
      if (acc >= nextBoss) {
        const p = around(380);
        spawn('boss', p.x, p.y);
        nextBoss += 120;
        arcadeAudio.blip(90, 0.3, 'sawtooth', 0.1);
      }

      emberCd -= dt; rootCd -= dt; thornCd -= dt; novaCd -= dt;
      const n = nearest();
      if (player.wep.ember && emberCd <= 0 && n) {
        emberCd = Math.max(0.16, 0.42 - player.wep.ember * 0.04);
        const a = Math.atan2(n.y - player.y, n.x - player.x);
        bolts.push({
          x: player.x, y: player.y,
          vx: Math.cos(a) * 420, vy: Math.sin(a) * 420,
          r: 5, dmg: 7 + player.wep.ember * 2, life: 1.1, pierce: 1, hue: '#ffb020',
        });
      }
      if (player.wep.thorn && thornCd <= 0 && n) {
        thornCd = 0.7 - player.wep.thorn * 0.06;
        const a = Math.atan2(n.y - player.y, n.x - player.x);
        bolts.push({
          x: player.x, y: player.y,
          vx: Math.cos(a) * 520, vy: Math.sin(a) * 520,
          r: 6, dmg: 10 + player.wep.thorn * 4, life: 1.3, pierce: 2 + player.wep.thorn, hue: '#7cffb2',
        });
      }
      if (player.wep.roots && rootCd <= 0) {
        rootCd = Math.max(1.4, 3.2 - player.wep.roots * 0.25);
        for (const e of [...enemies]) {
          if ((e.x - player.x) ** 2 + (e.y - player.y) ** 2 < (130 + player.wep.roots * 18) ** 2) {
            hurt(e, 14 + player.wep.roots * 6);
          }
        }
        burst(player.x, player.y, '#7c5cff', 18);
      }
      if (player.wep.nova && novaCd <= 0) {
        novaCd = 2.6;
        for (let i = 0; i < 10 + player.wep.nova * 2; i++) {
          const a = (i / (10 + player.wep.nova * 2)) * Math.PI * 2;
          bolts.push({
            x: player.x, y: player.y,
            vx: Math.cos(a) * 280, vy: Math.sin(a) * 280,
            r: 6, dmg: 8 + player.wep.nova * 3, life: 0.8, pierce: 2, hue: '#ff5d8f',
          });
        }
      }

      for (const o of orbs) {
        o.ang += dt * (2.2 + player.wep.spore * 0.25);
        const ox = player.x + Math.cos(o.ang) * o.dist;
        const oy = player.y + Math.sin(o.ang) * o.dist;
        for (const e of [...enemies]) {
          if ((e.x - ox) ** 2 + (e.y - oy) ** 2 < (e.r + 10) ** 2) hurt(e, o.dmg * dt * 8);
        }
      }

      for (let i = bolts.length - 1; i >= 0; i--) {
        const b = bolts[i];
        b.x += b.vx * dt; b.y += b.vy * dt; b.life -= dt;
        for (const e of [...enemies]) {
          if ((e.x - b.x) ** 2 + (e.y - b.y) ** 2 < (e.r + b.r) ** 2) {
            hurt(e, b.dmg);
            b.pierce -= 1;
            if (b.pierce <= 0) { b.life = 0; break; }
          }
        }
        if (b.life <= 0) bolts.splice(i, 1);
      }

      if (enemies.length > 220) enemies.splice(0, enemies.length - 220);

      for (const e of enemies) {
        const a = Math.atan2(player.y - e.y, player.x - e.x);
        e.x += Math.cos(a) * e.spd * dt;
        e.y += Math.sin(a) * e.spd * dt;
        e.flash = Math.max(0, e.flash - dt);
        const d2 = (e.x - player.x) ** 2 + (e.y - player.y) ** 2;
        if (d2 < (e.r + player.r) ** 2 && player.inv <= 0 && player.dash <= 0) {
          player.hp -= e.dmg;
          player.inv = 0.55;
          iAcc = 0;
          burst(player.x, player.y, '#ff5d8f', 10);
          arcadeAudio.blip(140, 0.1, 'sawtooth', 0.08);
          if (player.hp <= 0) {
            finish(false);
            return;
          }
        }
      }

      if (player.inv <= 0) iAcc += dt;
      iFramesBest = Math.max(iFramesBest, iAcc);

      for (let i = gems.length - 1; i >= 0; i--) {
        const g = gems[i];
        const dx = player.x - g.x; const dy = player.y - g.y;
        const d = Math.hypot(dx, dy);
        if (d < player.magnet) {
          g.x += (dx / (d || 1)) * 320 * dt;
          g.y += (dy / (d || 1)) * 320 * dt;
        }
        if (d < 18) {
          player.xp += g.v;
          gems.splice(i, 1);
          while (player.xp >= player.need) {
            player.xp -= player.need;
            player.lv += 1;
            player.need = Math.floor(12 + player.lv * 7);
            offer();
          }
        }
      }

      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        p.x += p.vx * dt; p.y += p.vy * dt; p.life -= dt;
        if (p.life <= 0) parts.splice(i, 1);
      }

      hud.current = {
        hp: player.hp, max: player.max, lv: player.lv, xp: player.xp, need: player.need,
        t: acc, kills, dash: player.dashCd <= 0 ? 1 : 1 - player.dashCd / 1.15,
      };
    };

    const finish = (won: boolean) => {
      overRef.current = true;
      const score = Math.floor(acc * 12 + kills * 8 + player.lv * 40);
      const note = won ? 'The Night Root bows.' : 'The grove swallows the lantern.';
      onResult({
        score, won,
        stamps: {
          clear: won,
          skill: iFramesBest >= 90,
          mastery: won && player.lv >= 14,
        },
        note,
      });
      setOver({ score, won, note });
      arcadeAudio.play(won ? 'win' : 'lose');
      arcadeAudio.blip(won ? 520 : 120, 0.25, won ? 'triangle' : 'sawtooth', 0.12);
    };

    const draw = () => {
      const camX = player.x - W / 2;
      const camY = player.y - H / 2;
      const grd = ctx.createRadialGradient(W * 0.5, H * 0.4, 40, W * 0.5, H * 0.5, 760);
      grd.addColorStop(0, '#243528');
      grd.addColorStop(1, '#0c120f');
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, W, H);
      ctx.save();
      ctx.translate(-camX, -camY);
      for (let i = 0; i < 90; i++) {
        const sx = ((i * 173) % 3200) - 1600;
        const sy = ((i * 97) % 3200) - 1600;
        ctx.fillStyle = i % 3 === 0 ? '#1a2a1e' : '#16231a';
        ctx.beginPath();
        ctx.ellipse(sx, sy, 70 + (i % 5) * 12, 40 + (i % 4) * 10, i, 0, 7);
        ctx.fill();
      }
      ctx.fillStyle = '#c9ff7a22';
      for (let i = 0; i < 40; i++) {
        const fx = ((i * 251 + acc * 12) % 3000) - 1500;
        const fy = ((i * 139 + Math.sin(acc + i) * 20) % 3000) - 1500;
        ctx.beginPath(); ctx.arc(fx, fy, 1.6, 0, 7); ctx.fill();
      }
      for (const g of gems) {
        const im = imgs.get('gem');
        if (im) ctx.drawImage(im, g.x - 10, g.y - 10, 20, 20);
        else {
          ctx.fillStyle = '#7c5cff';
          ctx.beginPath(); ctx.arc(g.x, g.y, 5, 0, 7); ctx.fill();
        }
      }
      for (const b of bolts) {
        ctx.fillStyle = b.hue;
        ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, 7); ctx.fill();
      }
      for (const o of orbs) {
        const ox = player.x + Math.cos(o.ang) * o.dist;
        const oy = player.y + Math.sin(o.ang) * o.dist;
        ctx.fillStyle = '#c9ff7a';
        ctx.beginPath(); ctx.arc(ox, oy, 9, 0, 7); ctx.fill();
      }
      for (const e of enemies) {
        const key = e.kind;
        const im = imgs.get(key);
        const s = e.r * 2.4;
        ctx.save();
        if (e.flash > 0) ctx.globalAlpha = 0.5;
        if (im) ctx.drawImage(im, e.x - s / 2, e.y - s / 2, s, s);
        else {
          ctx.fillStyle = e.kind === 'boss' ? '#ffb020' : e.kind === 'flyer' ? '#7c5cff' : '#5d8f4a';
          ctx.beginPath(); ctx.arc(e.x, e.y, e.r, 0, 7); ctx.fill();
        }
        ctx.restore();
        if (e.kind === 'boss' || e.hp < e.max) {
          ctx.fillStyle = '#0008';
          ctx.fillRect(e.x - e.r, e.y - e.r - 8, e.r * 2, 4);
          ctx.fillStyle = '#ff5d8f';
          ctx.fillRect(e.x - e.r, e.y - e.r - 8, e.r * 2 * (e.hp / e.max), 4);
        }
      }
      const moving = Math.hypot(inp.axis().x, inp.axis().y) > 0.1;
      const hero = imgs.get(moving && ((acc * 8) | 0) % 2 ? 'walkB' : moving ? 'walkA' : 'hero');
      if (hero) ctx.drawImage(hero, player.x - 22, player.y - 28, 44, 52);
      else {
        ctx.fillStyle = '#c9a6ff';
        ctx.beginPath(); ctx.arc(player.x, player.y, player.r, 0, 7); ctx.fill();
      }
      if (player.dashCd <= 0) {
        ctx.strokeStyle = '#ffffff55';
        ctx.beginPath(); ctx.arc(player.x, player.y, 22, 0, 7); ctx.stroke();
      }
      for (const p of parts) {
        ctx.globalAlpha = Math.max(0, p.life * 2);
        ctx.fillStyle = p.c;
        ctx.fillRect(p.x, p.y, p.s, p.s);
        ctx.globalAlpha = 1;
      }
      ctx.restore();
      const vig = ctx.createRadialGradient(W / 2, H / 2, 180, W / 2, H / 2, 520);
      vig.addColorStop(0, '#0000');
      vig.addColorStop(1, '#0006');
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, W, H);

      const t = Math.max(0, WIN - acc);
      const mm = String(Math.floor(t / 60)).padStart(2, '0');
      const ss = String(Math.floor(t % 60)).padStart(2, '0');
      ctx.fillStyle = 'rgba(12,18,14,0.62)';
      ctx.beginPath();
      ctx.roundRect(16, 14, 248, 92, 14);
      ctx.fill();
      ctx.fillStyle = '#fff4df';
      ctx.font = '700 26px Fraunces, serif';
      ctx.fillText(`${mm}:${ss}`, 28, 44);
      ctx.fillStyle = '#0008';
      ctx.fillRect(28, 56, 220, 10);
      ctx.fillStyle = '#ff5d8f';
      ctx.fillRect(28, 56, 220 * (player.hp / player.max), 10);
      ctx.fillStyle = '#0008';
      ctx.fillRect(28, 72, 220, 6);
      ctx.fillStyle = '#e0b15b';
      ctx.fillRect(28, 72, 220 * (player.xp / player.need), 6);
      ctx.fillStyle = '#b7a994';
      ctx.font = '12px Outfit, sans-serif';
      ctx.fillText(`Lv ${player.lv}   ${kills} fallen   best ${bestScore('bloomfall').toLocaleString()}`, 24, 96);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPaused((p) => !p);
      if (e.key === 'p' || e.key === 'P') setPaused((p) => !p);
    };
    window.addEventListener('keydown', onKey);
    raf = requestAnimationFrame(loop);

    return () => {
      dead = true;
      cancelAnimationFrame(raf);
      inp.dispose();
      window.removeEventListener('keydown', onKey);
      arcadeAudio.stopMusic();
    };
  }, [onResult]);

  const h = hud.current;
  return (
    <div className="game-root bloomfall">
      <canvas ref={ref} width={W} height={H} />
      <TouchPad
        input={input.current}
        extra={[{ id: 'dash', label: 'Dash', on: () => { input.current.actions.a = true; }, off: () => { input.current.actions.a = false; } }]}
      />
      {choices.length > 0 && (
        <div className="overlay">
          <div className="panel">
            <h2>The grove offers a bloom</h2>
            <p>Level {h.lv}. Pick one.</p>
            <div className="choices">
              {choices.map((c) => (
                <button key={c.id} type="button" className="play" onClick={() => pickRef.current(c.id)}>
                  <strong>{c.name}</strong>
                  <div>{c.blurb}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {paused && !over && (
        <div className="overlay">
          <div className="panel">
            <h2>Paused</h2>
            <p>Esc to resume. The Night Root waits twelve minutes.</p>
            <button type="button" className="play" onClick={() => setPaused(false)}>Resume</button>
            <button type="button" className="ghost" onClick={onExit} style={{ marginTop: 8, width: '100%' }}>Lobby</button>
          </div>
        </div>
      )}
      {over && (
        <div className="overlay">
          <div className="panel">
            <h2>{over.won ? 'Dawn' : 'Fallen'}</h2>
            <p>{over.note}</p>
            <p>Score {over.score.toLocaleString()}</p>
            <button type="button" className="play" onClick={() => location.reload()}>Again</button>
            <button type="button" className="ghost" onClick={onExit} style={{ marginTop: 8, width: '100%' }}>Lobby</button>
          </div>
        </div>
      )}
    </div>
  );
}
